/**
 * Error Classifier Service
 * 
 * Classifies HTTP errors and determines appropriate recovery actions.
 * Provides consistent error handling across the application.
 * 
 * Issue: #202
 */

import { Injectable } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'

import { log } from '@/util/logger'

export enum ErrorType {
  Unknown = 'unknown',
  Network = 'network',
  TokenExpired = 'token_expired',
  RefreshTokenExpired = 'refresh_token_expired',
  RateLimited = 'rate_limited',
  ClientError = 'client_error',
  ServerError = 'server_error',
  Timeout = 'timeout'
}

export enum ErrorAction {
  Retry = 'retry',
  RefreshToken = 'refresh_token',
  Logout = 'logout',
  Propagate = 'propagate',
  Queue = 'queue'
}

export interface ErrorClassification {
  type: ErrorType
  recoverable: boolean
  action: ErrorAction
  retryAfter?: number
  message?: string
}

/**
 * Service for classifying errors and determining recovery strategies.
 */
@Injectable({ providedIn: 'root' })
export class ErrorClassifierService {
  constructor() {}

  /**
   * Classify an error and determine the appropriate action
   */
  classify(error: any): ErrorClassification {
    // Handle HttpErrorResponse
    if (error instanceof HttpErrorResponse) {
      const classification = this._classifyHttpError(error)
      log('[ErrorClassifier] Classified HTTP error', {
        status: error.status,
        url: error.url,
        type: classification.type,
        action: classification.action,
        recoverable: classification.recoverable,
        retryAfter: classification.retryAfter,
        message: classification.message
      })
      return classification
    }

    // Handle network errors (no status code)
    if (error && error.status === 0) {
      const classification = {
        type: ErrorType.Network,
        recoverable: true,
        action: ErrorAction.Retry,
        retryAfter: 5000,
        message: 'Network error - connection failed'
      }
      log('[ErrorClassifier] Classified network error', classification)
      return classification
    }

    // Handle timeout errors
    if (error && error.name === 'TimeoutError') {
      return {
        type: ErrorType.Timeout,
        recoverable: true,
        action: ErrorAction.Retry,
        retryAfter: 3000,
        message: 'Request timeout'
      }
    }

    // Unknown error
    return {
      type: ErrorType.Unknown,
      recoverable: false,
      action: ErrorAction.Propagate,
      message: error?.message || 'Unknown error'
    }
  }

  /**
   * Classify HTTP errors
   */
  private _classifyHttpError(error: HttpErrorResponse): ErrorClassification {
    const status = error.status
    const errorMessage = error.error?.error || error.message || ''
    const errorMessageLower = errorMessage.toLowerCase()

    // Network errors (status 0 or 5xx)
    if (status === 0 || (status >= 500 && status < 600)) {
      return {
        type: status === 0 ? ErrorType.Network : ErrorType.ServerError,
        recoverable: true,
        action: ErrorAction.Retry,
        retryAfter: this._calculateRetryDelay(status),
        message: status === 0 ? 'Network error' : 'Server error'
      }
    }

    // Unauthorized (401)
    if (status === 401) {
      if (
        errorMessageLower.includes('signature has expired') ||
        errorMessageLower.includes('token expired') ||
        errorMessageLower.includes('jwt expired')
      ) {
        return {
          type: ErrorType.TokenExpired,
          recoverable: true,
          action: ErrorAction.RefreshToken,
          message: 'Access token expired'
        }
      }

      if (
        errorMessageLower.includes('expired refresh token') ||
        errorMessageLower.includes('invalid refresh token') ||
        errorMessageLower.includes('refresh token expired')
      ) {
        return {
          type: ErrorType.RefreshTokenExpired,
          recoverable: false,
          action: ErrorAction.Logout,
          message: 'Refresh token expired - please log in again'
        }
      }

      // Other 401 errors
      return {
        type: ErrorType.ClientError,
        recoverable: false,
        action: ErrorAction.Logout,
        message: 'Authentication failed'
      }
    }

    // Forbidden (403)
    if (status === 403) {
      return {
        type: ErrorType.ClientError,
        recoverable: false,
        action: ErrorAction.Propagate,
        message: 'Access forbidden'
      }
    }

    // Rate limiting (429)
    if (status === 429) {
      const retryAfter = this._parseRetryAfter(error)
      return {
        type: ErrorType.RateLimited,
        recoverable: true,
        action: ErrorAction.Retry,
        retryAfter: retryAfter,
        message: 'Rate limit exceeded'
      }
    }

    // Client errors (4xx)
    if (status >= 400 && status < 500) {
      return {
        type: ErrorType.ClientError,
        recoverable: false,
        action: ErrorAction.Propagate,
        message: errorMessage || `Client error (${status})`
      }
    }

    // Unknown status
    return {
      type: ErrorType.Unknown,
      recoverable: false,
      action: ErrorAction.Propagate,
      message: errorMessage || `Unknown error (${status})`
    }
  }

  /**
   * Calculate retry delay based on status code
   */
  private _calculateRetryDelay(status: number): number {
    if (status === 0) {
      // Network error - quick retry
      return 3000
    }

    if (status >= 500 && status < 600) {
      // Server error - exponential backoff
      // 503 (Service Unavailable) - shorter delay
      if (status === 503) {
        return 5000
      }
      // Other 5xx - longer delay
      return 10000
    }

    return 5000 // Default
  }

  /**
   * Parse Retry-After header or use default
   */
  private _parseRetryAfter(error: HttpErrorResponse): number {
    const retryAfterHeader = error.headers?.get('Retry-After')
    if (retryAfterHeader) {
      const seconds = parseInt(retryAfterHeader, 10)
      if (!isNaN(seconds)) {
        return seconds * 1000
      }
    }
    // Default: 60 seconds for rate limiting
    return 60000
  }

  /**
   * Check if an error is recoverable
   */
  isRecoverable(error: any): boolean {
    return this.classify(error).recoverable
  }

  /**
   * Get recommended action for an error
   */
  getAction(error: any): ErrorAction {
    return this.classify(error).action
  }
}
